<div class="pool-optimization-page">
  @if (loading) {
    <div class="state-card">
      <p-progressSpinner styleClass="w-4rem h-4rem"></p-progressSpinner>
      <p>Chargement des résultats d'optimisation...</p>
    </div>
  } @else if (error) {
    <div class="state-card error-state">
      <i class="pi pi-exclamation-circle"></i>
      <p>{{ error }}</p>
    </div>
  } @else {
    <div class="pool-layout">
      <!-- LEFT: Tour list -->
      <div class="left-column">
        <app-pool-tour-list
          [tours]="tours"
          [filteredTours]="filteredTours"
          [selectedTour]="selectedTour"
          [recalculatingTourKeys]="recalculatingTourKeys"
          [run]="run"
          [runCompleted]="runCompleted"
          [launchLoading]="launchLoading"
          [launchError]="launchError"
          [canClearSelection]="canClearSelection"
          [searchQuery]="searchQuery"
          [selectedGroupingName]="selectedGroupingName"
          [groupingNames]="groupingNames"
          [hiddenMapGroups]="hiddenMapGroups"
          [erosOnly]="erosOnly"
          (tourSelected)="selectTour($event)"
          (createAffairClicked)="openCreateAffairDialog()"
          (addExistingAffairClicked)="openAddExistingAffairDialog()"
          (clearSelectionAndSearch)="clearSelectionAndSearch()"
          (searchQueryChange)="searchQuery = $event"
          (selectedGroupingNameChange)="selectedGroupingName = $event"
          (groupFilterChanged)="onGroupFilterChange()"
          (erosOnlyChange)="erosOnly = $event"
        ></app-pool-tour-list>
      </div>

      <!-- CENTER: Context header + Map (full height) -->
      <div class="center-column center-full-height">
        <app-pool-tour-header
          [selectedTour]="selectedTour"
          [startStep]="startStep"
          [endStep]="endStep"
          [canOpenTablotFiche]="canOpenTablotFiche"
          [tablotFicheUrl]="tablotFicheUrl"
          [erosScheduleUrl]="erosScheduleUrl"
          [googleMapsUrl]="getGoogleMapsUrlForTour()"
          [manualScheduleAvailable]="manualScheduleAvailable"
          [manualDeleteAvailable]="manualDeleteAvailable"
          (manualScheduleRequested)="openManualScheduleForHighlighted()"
          (manualDeleteRequested)="onManualDeleteRequestedForHighlighted()"
        ></app-pool-tour-header>
        <div class="sector-bar">
          <span class="sector-bar-label">
            <i class="pi pi-map"></i>
            Secteur
          </span>
          <div class="sector-chips">
            <button
              type="button"
              class="sector-chip"
              [class.is-active]="!selectedSector"
              (click)="onSectorChange(null)"
            >
              Toute la France
              <span class="sector-chip-count">{{ totalPoolCount }}</span>
            </button>
            @for (sector of sectors; track sector.id) {
              <button
                type="button"
                class="sector-chip"
                [class.is-active]="selectedSector?.id === sector.id"
                [style.--chip-color]="sector.color || '#6366f1'"
                (click)="onSectorChange(sector.id)"
              >
                <span class="sector-chip-dot"></span>
                {{ sector.name }}
                <span class="sector-chip-count">{{ getSectorCount(sector) }}</span>
              </button>
            }
          </div>

          <label class="scheduled-toggle">
            <input
              type="checkbox"
              [checked]="showScheduledInterventions"
              (change)="toggleScheduledInterventions()"
            />
            <span class="si-legend-dot"></span>
            Interventions programmées
            @if (scheduledLoading) {
              <i class="pi pi-spin pi-spinner"></i>
            } @else if (showScheduledInterventions) {
              <span class="si-count">({{ scheduledInterventions.length }})</span>
            }
          </label>

          <div class="scheduled-search">
            <i class="pi pi-search"></i>
            <input
              type="text"
              [value]="scheduledSearchQuery"
              (input)="
                onScheduledSearchQueryChange($any($event.target).value)
              "
              placeholder="Rechercher (client, technicien, ville…)"
              aria-label="Rechercher une intervention programmée"
            />
            @if (scheduledSearchQuery) {
              <button
                type="button"
                class="scheduled-search-clear"
                (click)="clearScheduledSearch()"
                aria-label="Effacer la recherche"
                title="Effacer"
              >
                <i class="pi pi-times"></i>
              </button>
            }
          </div>
        </div>
        <p-card styleClass="map-card" class="map-card-host">
          <div class="map-shell">
            <app-tablot-map
              [affairs]="emptyAffairs"
              [interventions]="emptyInterventions"
              [focusZoom]="10"
              [tours]="mapTours"
              [selectedTour]="mapSelectedTour"
              [poolMarkers]="mapPoolMarkers"
              [technicianHomes]="mapTechnicianHomes"
              [sectors]="sectors"
              [selectedSectorId]="selectedSector?.id ?? null"
              [scheduledInterventions]="scheduledInterventions"
              [scheduledRoute]="scheduledRoute"
              [scheduledRouteOrder]="scheduledRouteOrder"
              (poolMarkerClicked)="onPoolMarkerClicked($event)"
              (scheduledInterventionClicked)="onScheduledInterventionClicked($event)"
              (citySelected)="onCitySelected($event)"
              (hiddenGroupsChange)="onHiddenGroupsChange($event)"
            ></app-tablot-map>
            @if (geometryLoading) {
              <div class="map-geometry-loading">
                <p-progressSpinner
                  styleClass="w-2rem h-2rem"
                  strokeWidth="4"
                ></p-progressSpinner>
                <span>Calcul de l'itinéraire...</span>
              </div>
            }
          </div>
        </p-card>
      </div>

      <!-- RIGHT: Day summary + scheduled-route recap (on click) + affairs / pool list -->
      <div class="right-column">
        @if (scheduledRouteRecap) {
          <app-pool-scheduled-route-recap
            [recap]="scheduledRouteRecap"
            [loading]="scheduledRouteLoading"
            (closed)="onScheduledRouteRecapClosed()"
          ></app-pool-scheduled-route-recap>
        }
        @if (selectedTour && selectedTourSteps.length > 0) {
          <app-pool-day-timeline
            [selectedTourSteps]="selectedTourSteps"
            [startStep]="startStep"
            [endStep]="endStep"
            [selectedTechnicianHomeAddress]="selectedTechnicianHomeAddress"
            [highlightedAffairId]="highlightedAffairId"
            [copiedAddressAffairId]="copiedAddressAffairId"
            [optimizingAffairIds]="optimizingAffairIds"
            [recalculating]="
              !!selectedTour && recalculatingTourKeys.has(selectedTour.tourKey)
            "
            [requiresDayShift]="selectedTourRequiresDayShift"
            [dayShiftMoves]="selectedTourDayShiftMoves"
            (timelineStepClick)="onTimelineAffairClick($event)"
            (addressCopied)="copyAffairAddress($event.affair, $event.event)"
            (alternativeSlotNavigated)="
              navigateToAlternativeSlot($event.slot, $event.affairId)
            "
            (manualScheduleRequested)="onManualScheduleRequested($event)"
            (manualDeleteRequested)="onManualDeleteRequested($event)"
          ></app-pool-day-timeline>
        }
        @if (!selectedTour) {
          @if (scheduledSearchQuery.trim().length > 0) {
            <app-pool-scheduled-search
              [query]="scheduledSearchQuery"
              [results]="scheduledSearchResults"
              [totalCount]="scheduledSearchTotalCount"
              [selectedInterventionId]="selectedScheduledSearchId"
              [loading]="scheduledLoading"
              (resultSelected)="onScheduledSearchResultSelected($event)"
              (closed)="clearScheduledSearch()"
            ></app-pool-scheduled-search>
          } @else if (searchedCity) {
            <app-pool-nearby-interventions
              [cityName]="searchedCity.name"
              [interventions]="nearbyInterventions"
              [radiusKm]="nearbyRadiusKm"
              [selectedInterventionId]="selectedNearbyInterventionId"
              [loading]="scheduledLoading"
              (interventionSelected)="onNearbyInterventionSelected($event)"
              (closed)="closeNearbyInterventions()"
            ></app-pool-nearby-interventions>
          } @else {
            <app-pool-no-slot-affairs
              [poolAffairsWithoutSlots]="poolAffairsWithoutSlots"
              [filteredPoolAffairsWithoutSlots]="filteredPoolAffairsWithoutSlots"
              [poolSearchQuery]="poolSearchQuery"
              [selectedNoSlotAffairId]="selectedNoSlotAffairId"
              [copiedAddressAffairId]="copiedAddressAffairId"
              (poolSearchQueryChange)="poolSearchQuery = $event"
              (noSlotAffairSelected)="onNoSlotAffairSelected($event)"
              (addressCopied)="copyAffairAddress($event.affair, $event.event)"
              (manualDeleteRequested)="onManualDeleteRequestedForNoSlot($event)"
            ></app-pool-no-slot-affairs>
          }
        }
      </div>
    </div>
  }

  <!-- Création manuelle d'une affaire vivier (prise d'appel) -->
  <app-pool-affair-create-dialog
    [(visible)]="createAffairDialogVisible"
    (created)="onPoolAffairCreated($event)"
  ></app-pool-affair-create-dialog>

  <!-- Ajout d'une affaire EROS existante au vivier -->
  <app-pool-affair-add-existing-dialog
    [(visible)]="addExistingAffairDialogVisible"
    (created)="onExistingAffairAdded($event)"
  ></app-pool-affair-add-existing-dialog>

  <!-- Finalisation d'un draft : créneau + agence → affaire + intervention EROS -->
  <app-pool-affair-schedule-dialog
    [context]="manualScheduleContext"
    (closed)="onManualScheduleClosed()"
    (scheduled)="onPoolAffairScheduled($event)"
  ></app-pool-affair-schedule-dialog>

  <!-- Confirmation de suppression d'une affaire manuelle -->
  <p-confirmDialog [style]="{ width: '420px' }"></p-confirmDialog>
</div>
